import React from 'react';
import { Button, Empty, Popconfirm, Table, Tag, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Expense, formatCents } from '../../services/moneyApi';

const { Text } = Typography;

const CATEGORY_COLOR: Record<string, string> = {
  parts: 'blue',
  supplies: 'cyan',
  fuel: 'orange',
  tools: 'purple',
  subscriptions: 'geekblue',
  other: 'default',
};

interface ExpenseListProps {
  expenses: Expense[];
  loading?: boolean;
  onDelete?: (id: string) => void;
}

/**
 * Expense list table. Owner-only (caller ensures role guard).
 */
const ExpenseList: React.FC<ExpenseListProps> = ({ expenses, loading, onDelete }) => {
  const columns = [
    {
      title: 'Date',
      dataIndex: 'expenseDate',
      key: 'expenseDate',
      width: 110,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 110,
      render: (cat: string) => (
        <Tag color={CATEGORY_COLOR[cat] ?? 'default'}>
          {cat.charAt(0).toUpperCase() + cat.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amountCents',
      key: 'amountCents',
      width: 100,
      render: (cents: number) => (
        <Text strong style={{ color: '#dc2626' }}>{formatCents(cents)}</Text>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (desc: string | null) => desc ?? <Text type="secondary">—</Text>,
    },
    {
      title: 'WO#',
      dataIndex: 'workOrderId',
      key: 'workOrderId',
      width: 80,
      render: (woId: number | null) =>
        woId ? <Text type="secondary">#{woId}</Text> : <Text type="secondary">—</Text>,
    },
    ...(onDelete
      ? [
          {
            title: '',
            key: 'actions',
            width: 60,
            render: (_: unknown, record: Expense) => (
              <Popconfirm
                title="Delete this expense?"
                onConfirm={() => onDelete(record.id)}
                okType="danger"
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  data-testid={`expense-row-${record.id}`}
                />
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <Table
      dataSource={expenses}
      columns={columns}
      rowKey="id"
      loading={loading}
      size="small"
      pagination={{ pageSize: 20, hideOnSinglePage: true }}
      locale={{ emptyText: <Empty description="No expenses recorded" /> }}
      scroll={{ x: true }}
    />
  );
};

export default ExpenseList;
